// src/index.tsx
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';
import { onCLS, onLCP, onINP } from 'web-vitals';

import App from './App';
import { trackWebVitals } from './components/GoogleAnalytics';
import { queryClient } from './queryClient';
import { darkTheme, lightTheme } from './theme';

const Main = () => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : true;
  });

  const handleToggle = () => {
    setDarkMode((prevMode: boolean) => {
      const newMode = !prevMode;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      return newMode;
    });
  };

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode]);

  const theme = darkMode ? darkTheme : lightTheme;

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <Router>
            <App toggleTheme={handleToggle} darkMode={darkMode} />
          </Router>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <Main />
  </React.StrictMode>,
);

// Initialize Core Web Vitals tracking (skip during prerendering)
// Metrics are sent to GA4 for monitoring performance
// react-snap sets navigator.userAgent to include "ReactSnap" during prerendering
const isPrerendering = navigator.userAgent.includes('ReactSnap');
if (!isPrerendering) {
  onCLS(trackWebVitals);
  onLCP(trackWebVitals);
  onINP(trackWebVitals);
}
